import { getAddress, type Address, type Hex } from "viem";
import type { Eip1193Provider } from "./eip1193";

type ProviderListener = (...args: unknown[]) => void;

let singleton: Eip1193Provider | null | undefined;

/**
 * Browser-only wallet adapter for the isolated local acceptance harness.
 *
 * The adapter is deliberately unreachable in production builds and refuses
 * non-loopback page/RPC origins.  Transactions still execute on the real
 * local EVM through JSON-RPC; only account selection and SIWE signing use the
 * explicitly supplied disposable test key.
 */
export function localE2eProvider(): Eip1193Provider | null {
  if (singleton !== undefined) return singleton;
  if (!import.meta.env.DEV || import.meta.env.VITE_LOCAL_E2E_TEST_ONLY !== "true") {
    singleton = null;
    return singleton;
  }

  const rpcUrl = import.meta.env.VITE_LOCAL_E2E_RPC_URL;
  const privateKey = import.meta.env.VITE_LOCAL_E2E_PRIVATE_KEY;
  const expectedAccount = import.meta.env.VITE_LOCAL_E2E_ACCOUNT;
  if (!rpcUrl || !privateKey || !expectedAccount || !isPrivateKey(privateKey)) {
    throw new Error("LOCAL_E2E_TEST_ONLY wallet configuration is incomplete");
  }
  const rpc = new URL(rpcUrl);
  if (!isLoopback(location.hostname) || !isLoopback(rpc.hostname)) {
    throw new Error("LOCAL_E2E_TEST_ONLY wallet is restricted to loopback origins");
  }

  singleton = new LocalE2eProvider(rpc.toString(), privateKey, getAddress(expectedAccount));
  return singleton;
}

class LocalE2eProvider implements Eip1193Provider {
  private readonly listeners = new Map<string, Set<ProviderListener>>();
  private requestId = 0;
  private accountPromise: Promise<Awaited<ReturnType<typeof import("viem/accounts")["privateKeyToAccount"]>>> | null = null;

  constructor(
    private readonly rpcUrl: string,
    private readonly privateKey: Hex,
    private readonly expectedAccount: Address,
  ) {}

  async request(args: { method: string; params?: readonly unknown[] | object }): Promise<unknown> {
    if (args.method === "eth_requestAccounts" || args.method === "eth_accounts") {
      return [(await this.account()).address];
    }
    if (args.method === "personal_sign" || args.method === "eth_sign") {
      return await this.signMessage(args.params);
    }
    if (args.method === "wallet_switchEthereumChain") {
      const requested = readSwitchChainId(args.params);
      const actual = await this.forward("eth_chainId", []);
      if (typeof actual !== "string" || BigInt(actual) !== requested) {
        const error = new Error(`LOCAL_E2E_TEST_ONLY chain ${requested} is unavailable`) as Error & { code: number };
        error.code = 4902;
        throw error;
      }
      this.emit("chainChanged", actual);
      return null;
    }
    return await this.forward(args.method, args.params ?? []);
  }

  on(event: "accountsChanged" | "chainChanged" | "disconnect", listener: ProviderListener): void {
    const listeners = this.listeners.get(event) ?? new Set<ProviderListener>();
    listeners.add(listener);
    this.listeners.set(event, listeners);
  }

  removeListener(event: "accountsChanged" | "chainChanged" | "disconnect", listener: ProviderListener): void {
    this.listeners.get(event)?.delete(listener);
  }

  private async account() {
    this.accountPromise ??= import("viem/accounts").then(({ privateKeyToAccount }) => {
      const account = privateKeyToAccount(this.privateKey);
      if (account.address.toLowerCase() !== this.expectedAccount.toLowerCase()) {
        throw new Error("LOCAL_E2E_TEST_ONLY private key/account mismatch");
      }
      return account;
    });
    return await this.accountPromise;
  }

  private async signMessage(params: readonly unknown[] | object | undefined): Promise<Hex> {
    if (!Array.isArray(params) || params.length < 2 || typeof params[0] !== "string" ||
      typeof params[1] !== "string") throw new Error("LOCAL_E2E_TEST_ONLY received malformed sign params");
    const first = params[0];
    const second = params[1];
    const addressValue = /^0x[0-9a-fA-F]{40}$/.test(first) ? first : second;
    const messageValue = addressValue === first ? second : first;
    if (getAddress(addressValue).toLowerCase() !== this.expectedAccount.toLowerCase()) {
      throw new Error("LOCAL_E2E_TEST_ONLY refused signing for a different account");
    }
    const account = await this.account();
    return await account.signMessage({
      message: /^0x(?:[0-9a-fA-F]{2})*$/.test(messageValue)
        ? { raw: messageValue as Hex }
        : messageValue,
    });
  }

  private async forward(method: string, params: readonly unknown[] | object): Promise<unknown> {
    const response = await fetch(this.rpcUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jsonrpc: "2.0", id: ++this.requestId, method, params }),
    });
    if (!response.ok) throw new Error(`LOCAL_E2E_TEST_ONLY RPC HTTP ${response.status}`);
    const payload = await response.json() as { result?: unknown; error?: { code?: number; message?: string } };
    if (payload.error) {
      const error = new Error(payload.error.message ?? "LOCAL_E2E_TEST_ONLY RPC error") as Error & { code?: number };
      if (payload.error.code !== undefined) error.code = payload.error.code;
      throw error;
    }
    return payload.result;
  }

  private emit(event: string, value: unknown): void {
    for (const listener of this.listeners.get(event) ?? []) listener(value);
  }
}

function readSwitchChainId(params: readonly unknown[] | object | undefined): bigint {
  if (!Array.isArray(params) || !params[0] || typeof params[0] !== "object") {
    throw new Error("LOCAL_E2E_TEST_ONLY received malformed switch-chain params");
  }
  const chainId = (params[0] as { chainId?: unknown }).chainId;
  if (typeof chainId !== "string" || !/^0x[0-9a-f]+$/i.test(chainId)) {
    throw new Error("LOCAL_E2E_TEST_ONLY received an invalid chain ID");
  }
  return BigInt(chainId);
}

function isLoopback(hostname: string): boolean {
  const normalized = hostname.toLowerCase().replace(/^\[|\]$/g, "");
  return normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1";
}

function isPrivateKey(value: string): value is Hex {
  return /^0x[0-9a-fA-F]{64}$/.test(value);
}
